import React from 'react';
import { Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { ReportData } from '../../../types/interfaces';
import { useTranslation } from 'react-i18next';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

interface VulnerabilitiesChartProps {
  summaryData: ReportData;
}

const VulnerabilitiesChart: React.FC<VulnerabilitiesChartProps> = ({ summaryData }) => {
  const { t } = useTranslation();
  const data = processVulnerabilityData(summaryData);

  // Traducir las etiquetas usando las claves de severidad
  const translatedLabels = data.labels.map((label: string) => t(`severity.${label}`));

  const chartData = {
    labels: translatedLabels,
    datasets: [
      {
        data: data.counts,
        backgroundColor: data.colors,
        borderColor: 'white', // Borde blanco
        borderWidth: 2,       
        hoverOffset: 10,      
      },
    ],
  };

  const options: ChartOptions<'pie'> = {
    plugins: {
      datalabels: {
        color: '#fff',
        font: {
          weight: 'bold',
          size: 30,
        },
        formatter: (value: any) => value,
      },
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: 18,
          },
        },
      },
      tooltip: {
        bodyFont: {
          size: 14,
        },
        callbacks: {
          label: (context: any) => {
            const label = context.label || '';
            const value = context.raw || '';
            return `${label}: ${value}`;
          },
        },
      },
    },
    maintainAspectRatio: false,
  };

  return (
    <div className="my-10" style={{ width: '100%', height: '500px' }}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

export default VulnerabilitiesChart;

const processVulnerabilityData = (summaryData: ReportData) => {
  const data = [
    {
      name: 'Trabajadores encontrados',
      value: summaryData.num_trabajadores,
      severity:
        summaryData.num_trabajadores > 5
          ? 'high'
          : summaryData.num_trabajadores > 0
          ? 'medium'
          : 'correct',
    },
    {
      name: 'IPs detectadas',
      value: summaryData.num_ips,
      severity: summaryData.num_ips > 0 ? 'medium' : 'correct',
    },
    {
      name: 'Emails detectados',
      value: summaryData.num_emails,
      severity:
        summaryData.num_emails > 5
          ? 'high'
          : summaryData.num_emails > 0
          ? 'medium'
          : 'correct',
    },
    {
      name: 'Filtraciones de datos',
      value: summaryData.num_filtraciones,
      severity: summaryData.num_filtraciones > 0 ? 'critical' : 'correct',
    },
    {
      name: 'Subdominios detectados',
      value: summaryData.num_subdomains,
      severity: summaryData.num_subdomains > 0 ? 'medium' : 'correct',
    },
    {
      name: 'Typosquatting detectado',
      value: summaryData.num_typosquatting,
      severity: summaryData.num_typosquatting > 0 ? 'medium' : 'correct',
    },
    {
      name: 'Dominios spoofeables',
      value: summaryData.num_dominios_spoofeables,
      severity: summaryData.num_dominios_spoofeables > 0 ? 'critical' : 'correct',
    },
    {
      name: 'Vulnerabilidades en IPs',
      value: summaryData.len_vulns,
      severity:
        summaryData.len_vulns > 4
          ? 'high'
          : summaryData.len_vulns > 0
          ? 'medium'
          : 'correct',
    },
  ];

  // Añadir datos relacionados con URLs si hay directorios
  if (summaryData.num_directories > 0) {
    const dataWithUrl = [
      {
        name: 'Tecnologías detectadas',
        value: summaryData.num_technologies,
        severity: summaryData.num_technologies > 0 ? 'low' : 'correct',
      },
      {
        name: 'Directorios detectados',
        value: summaryData.num_directories,
        severity: summaryData.num_directories > 0 ? 'low' : 'correct',
      },
      {
        name: 'Directorios sensibles',
        value: summaryData.len_dir_sensibles,
        severity: summaryData.len_dir_sensibles > 0 ? 'high' : 'correct',
      },
      {
        name: 'URLs sin WAF',
        value: summaryData.num_urls_without_waf,
        severity: summaryData.num_urls_without_waf > 0 ? 'high' : 'correct',
      },
    ];

    data.push(...dataWithUrl);
  }

  // Filtrar sólo las severidades que no son "correct" (se consideran no problemáticas)
  const severities = data
    .filter((item) => item.severity !== 'correct')
    .map((item) => item.severity);

  const severityCounts = severities.reduce((acc: any, severity: string) => {
    acc[severity] = (acc[severity] || 0) + 1;
    return acc;
  }, {});

  const severityOrder = ['critical', 'high', 'medium', 'low'];

  const labels = severityOrder.filter((sev) => severityCounts[sev]).map((sev) => sev);
  const counts = labels.map((label) => severityCounts[label]);

  const colorsMap: { [key: string]: string } = {
    critical: 'rgba(255, 35, 38, 0.9)',
    high: 'rgba(255, 130, 51, 0.9)',
    medium: 'rgba(253, 203, 73, 0.9)',
    low: 'rgba(201, 255, 83, 0.9)',
  };

  const colors = labels.map((label) => colorsMap[label]);

  return { labels, counts, colors };
};
